import { archiveGallery } from "./archive-design";
import { installResponsivePage, responsivePageCss } from "./responsive-page";
import { readerDevice, dissolveLayout } from "./reader-layout";
import { installMediaPlayers, stopMediaPlayers } from "./media-controls";
import { pageViewportCss, installPageViewport } from "./archive-viewport";
import { installFonts } from "./font-loader";
import { pageRenderTheme, applyPageLayerPresentation } from "./page-layers";
import { configureBackdrop } from "./backdrop";
import { trackArtworkScale, artworkScalingCss } from "./artwork-scale";
import {
  renderBlocks,
  layoutCss as baseLayoutCss,
  deviceLayoutCss,
  renderSafeBoxes,
  safeBoxCss,
} from "./layout";
const layoutCss =
  baseLayoutCss +
  deviceLayoutCss +
  `.page-block{--active-stroke-dash:var(--bstroke-dash,none)}@media(max-width:640px){.page-block{--active-stroke-dash:var(--mstroke-dash,none)}}`;
import { installPageBackground, readerBackgroundCss } from "./page-background";
const escape = (s: unknown) =>
  String(s ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
const legacyContentCss = `
.scene .page-content{box-sizing:border-box;position:absolute;left:0;top:calc(var(--reading-top,0px)*var(--artwork-scale,1) + var(--reading-offset,0)*1cqw + var(--artwork-offset,0px));width:var(--panel-percent,100%);max-width:calc(var(--panel-width,820px)*var(--artwork-scale,1));margin-left:calc((100% - min(var(--panel-percent,100%),calc(var(--panel-width,820px)*var(--artwork-scale,1))))*var(--panel-x,.5));padding:calc(var(--panel-padding,64px)*var(--artwork-scale,1));pointer-events:none}
.scene .page-content:has(.archive-grid){position:relative;top:0;margin-top:calc(var(--reading-top,0px)*var(--artwork-scale,1) + var(--reading-offset,0)*1cqw + var(--artwork-offset,0px))}
@media(max-width:640px){.scene .page-content{width:var(--mobile-panel-percent,100%);margin-left:calc((100% - min(var(--mobile-panel-percent,100%),calc(var(--panel-width,820px)*var(--artwork-scale,1))))*var(--panel-mobile-x,.5));padding:calc(var(--panel-mobile-padding,22px)*var(--artwork-scale,1))}}
`;
const style = `:host{display:block;width:100%;color:#242722;font:16px/1.7 system-ui,sans-serif}*{box-sizing:border-box}a,button{touch-action:manipulation}button,a.button{font:600 14px/1.4 system-ui;cursor:pointer;min-height:44px;display:inline-flex;align-items:center;justify-content:center;padding:12px 22px;border-radius:var(--radius,20px);border:1px solid var(--primary);background:var(--buttonBackground,var(--primary));color:var(--buttonText,white);text-decoration:none;gap:8px}a{color:var(--accent)}button:focus-visible,a:focus-visible{outline:3px solid #0076ab;outline-offset:4px}.scene{container-type:inline-size;position:relative;isolation:isolate;min-height:640px;padding:calc(var(--hero-gap,120px) + var(--panel-offset,0%)) 24px 70px;background:var(--secondary,var(--fallback,#e9e2d4));color:var(--text)}.scene::before{content:"";position:absolute;inset:0;z-index:-1;opacity:var(--background-opacity,1);background-image:linear-gradient(rgb(0 0 0 / var(--darkness)),rgb(0 0 0 / var(--darkness))),var(--desktop);background-size:cover;background-position:var(--x) var(--y);color:var(--text)}.panel{min-height:calc(var(--panel-min-height,0) * 1cqw);border:1px solid var(--secondary);width:var(--panel-percent,100%);max-width:var(--panel-width,820px);margin-left:calc((100% - min(var(--panel-percent,100%),var(--panel-width,820px))) * var(--panel-x,.5));margin-right:0;border-radius:var(--radius);padding:var(--panel-padding,64px);background:color-mix(in srgb,var(--surface) calc(var(--opacity)*100%),transparent);box-shadow:0 24px 80px #0002}header{margin-bottom:36px;border-bottom:1px solid #7773;padding-bottom:24px}.brand{font-size:11px;letter-spacing:.22em;text-transform:uppercase;margin:0 0 10px}.series{font-family:var(--heading);font-size:clamp(26px,4vw,36px);line-height:1.2;margin:0}.eyebrow{letter-spacing:.16em;font-size:11px;font-weight:700;color:var(--accent);margin:0 0 12px}h2{font-family:var(--heading);font-size:clamp(30px,5vw,48px);font-weight:500;line-height:1.13;margin:12px 0 18px}.subtitle{font-size:19px;color:inherit;opacity:.8}.prose{font-family:Georgia,serif;font-size:19px;line-height:1.85;overflow-wrap:anywhere}.prose p{margin:0 0 1.4em}.prose h2{font-size:28px}.prose h3{font-size:23px}nav{margin-top:36px;padding-top:24px;border-top:1px solid #7773}.grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px}.card{display:flex;flex-direction:column;align-items:flex-start;text-align:left;background:var(--surface);color:var(--text);padding:22px;border:1px solid #7774;box-shadow:0 2px 5px #0001}.card:hover{transform:translateY(-2px);box-shadow:0 8px 18px #0001}.card strong{font-family:var(--heading);font-size:21px;font-weight:500}.card small{margin-top:12px;font-weight:400}iframe{border:0;width:100%;min-height:240px;aspect-ratio:16/9;border-radius:12px;background:#f0efe9}.media-note{font:13px system-ui;opacity:.8}dialog{border:0;border-radius:20px;padding:0;max-width:840px;width:calc(100% - 32px);max-height:90dvh;color:var(--text);background:var(--surface)}dialog::backdrop{background:#10201cc2}dialog .inside{padding:28px clamp(20px,4vw,50px) 44px}.close{float:right;margin:12px}.notice{font:14px system-ui;color:var(--text);margin-top:15px}.empty{text-align:center;padding:25px 0 45px}.empty h2{font-size:34px}.empty p{max-width:480px;margin:20px auto}.skeleton{animation:pulse 1.6s infinite;min-height:240px;opacity:.6}@keyframes pulse{50%{opacity:.3}}@media(max-width:640px){.scene{padding:calc(var(--mobile-gap,200px) + var(--mobile-panel-offset,0%)) 16px 40px;min-height:700px}.scene::before{background-image:linear-gradient(rgb(0 0 0 / var(--darkness)),rgb(0 0 0 / var(--darkness))),var(--mobile,var(--desktop))}.panel{min-height:calc(var(--mobile-panel-min-height,0) * 1cqw);padding:var(--panel-mobile-padding,22px);width:var(--mobile-panel-percent,100%);margin-left:calc((100% - min(var(--mobile-panel-percent,100%),var(--panel-width,820px))) * var(--panel-mobile-x,.5))}.grid{grid-template-columns:1fr 1fr;gap:10px}.card{padding:16px}.card strong{font-size:18px}.prose{font-size:18px}header{margin-bottom:26px}}@media(prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}`;
class PinedaleDays extends HTMLElement {
  root = this.attachShadow({ mode: "open" });
  timer: ReturnType<typeof setInterval> | undefined;
  data: any;
  theme: any;
  seriesConfig: any;
  last = "";
  stopped = false;
  artworkObserver?: ResizeObserver;
  cardObservers?: ResizeObserver[];
  artworkRatio?: number;
  pageViewport?: ReturnType<typeof installPageViewport>;
  responsivePage?: ReturnType<typeof installResponsivePage>;
  layoutObserver?: ResizeObserver;
  refreshRevision = 0;
  renderedDevice = "";
  responsiveChanged = () => {
    if (!this.last || this.stopped) return;
    const device = readerDevice(this.theme, this.mode, this.clientWidth);
    if (device === this.renderedDevice) return;
    const dialog = this.root.querySelector<HTMLDialogElement>("dialog[open]");
    if (dialog) {
      dialog.addEventListener("close", this.responsiveChanged, { once: true });
      return;
    }
    void this.refresh(true, true);
  };
  deviceQuery = matchMedia("(max-width:640px)");
  deviceChanged = () => {
    const dialog = this.root.querySelector<HTMLDialogElement>("dialog[open]");
    if (dialog) {
      dialog.addEventListener("close", this.deviceChanged, { once: true });
      return;
    }
    void this.refresh(true, !!this.last);
  };
  updatePageBackground: ReturnType<typeof installPageBackground>;
  get api() {
    return (this.getAttribute("api") || location.origin).replace(/\/$/, "");
  }
  get series() {
    return this.getAttribute("series") || "christmas";
  }
  get mode() {
    return this.getAttribute("mode") === "archive" ? "archive" : "daily";
  }
  async get(path: string) {
    const r = await fetch(`${this.api}/api/public/${this.series}/${path}`, {
      cache: "no-store",
      credentials: "omit",
      signal: AbortSignal.timeout(15000),
    });
    if (r.status === 404) {
      const error = Error("This journey is no longer available.");
      error.name = "PageUnavailable";
      throw error;
    }
    if (!r.ok) throw Error("We couldn’t load this page. Please try again.");
    return r.json();
  }
  connectedCallback() {
    this.stopped = false;
    this.deviceQuery.addEventListener("change", this.deviceChanged);
    window.addEventListener("resize", this.responsiveChanged);
    this.layoutObserver = new ResizeObserver(this.responsiveChanged);
    this.layoutObserver.observe(this);
    this.root.innerHTML = `<style>${style}</style><section class="panel skeleton" aria-busy="true">Preparing your devotional…</section>`;
    void this.refresh();
    this.timer = setInterval(() => {
      if (!document.hidden) void this.refresh();
    }, 60000);
  }
  disconnectedCallback() {
    stopMediaPlayers(this.root);
    this.pageViewport?.destroy();
    this.responsivePage?.destroy();
    this.layoutObserver?.disconnect();
    this.stopped = true;
    this.artworkObserver?.disconnect();
    this.cardObservers?.forEach((observer) => observer.disconnect());
    this.deviceQuery.removeEventListener("change", this.deviceChanged);
    window.removeEventListener("resize", this.responsiveChanged);
    clearInterval(this.timer);
  }
  async refresh(force = false, cached = false) {
    const revision = ++this.refreshRevision;
    try {
      const [config, data] = cached
        ? [
            { series: this.seriesConfig, themes: { [this.mode]: this.theme } },
            this.data,
          ]
        : await Promise.all([
            this.get("config"),
            this.get(this.mode === "daily" ? "current" : "archive"),
          ]);
      if (this.stopped) return;
      const fingerprint = JSON.stringify([
        config.series,
        config.themes[this.mode],
        data.entry || data.entries,
      ]);
      if (fingerprint === this.last && !force) return;
      if (this.root.querySelector("dialog[open]")) return;
      const updated = !!this.last;
      const device = readerDevice(
        config.themes[this.mode],
        this.mode,
        this.clientWidth,
      );
      const theme = pageRenderTheme(config.themes[this.mode], device);
      const artwork =
        device === "mobile" ? theme.mobile || theme.desktop : theme.desktop;
      const image = new Image();
      const assets: (Promise<unknown> | undefined)[] = [
        installFonts(theme, this.api),
      ];
      if (artwork) {
        image.src = new URL(artwork, this.api).href;
        assets.push(image.decode().catch(() => undefined));
      }
      let deadline: ReturnType<typeof setTimeout>;
      await Promise.race([
        Promise.allSettled(assets),
        new Promise<void>((resolve) => {
          deadline = setTimeout(resolve, 10000);
        }),
      ]);
      clearTimeout(deadline!);
      if (this.stopped || revision !== this.refreshRevision) return;
      if (this.root.querySelector("dialog[open]")) return;
      this.last = fingerprint;
      this.data = data;
      this.theme = config.themes[this.mode];
      this.artworkRatio = image.naturalWidth
        ? image.naturalHeight / image.naturalWidth
        : undefined;
      await dissolveLayout(
        () => this.render(config.series, updated),
        cached && this.renderedDevice !== device,
      );
      if (revision !== this.refreshRevision) return;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      this.closest<HTMLElement>(".pinedale-days-mount")?.setAttribute(
        "data-pinedale-ready",
        "",
      );
      this.closest<HTMLElement>(".pinedale-days-mount")?.setAttribute(
        "data-ready-device",
        device,
      );
    } catch (e) {
      this.closest<HTMLElement>(".pinedale-days-mount")?.setAttribute(
        "data-pinedale-ready",
        "",
      );
      if ((e as Error).name === "PageUnavailable") {
        this.last = "";
        this.root.innerHTML = `<style>${style}</style><section class="panel empty"><h2>Page unavailable</h2><p>This journey is no longer available.</p></section>`;
        return;
      }
      if (!this.last) {
        this.root.innerHTML = `<style>${style}</style><section class="panel empty"><h2>A moment of patience</h2><p>${escape((e as Error).message)}</p><button>Try again</button></section>`;
        this.root.querySelector("button")!.onclick = () => void this.refresh();
      } else {
        const notice = this.root.querySelector(".notice");
        if (notice)
          notice.textContent =
            "Connection interrupted. Your reading remains available; we’ll retry shortly.";
      }
    }
  }
  render(series: any, updated: boolean) {
    const archiveScroll = this.pageViewport?.scrollTop ?? 0;
    stopMediaPlayers(this.root);
    this.pageViewport?.destroy();
    this.responsivePage?.destroy();
    this.responsivePage = undefined;
    this.seriesConfig = series;
    this.pageViewport = undefined;
    const device = readerDevice(this.theme, this.mode, this.clientWidth);
    const t = pageRenderTheme(this.theme, device);
    this.renderedDevice = device;
    const fallback =
      {
        christmas: "#733629",
        luke: "#dce9ee",
        acts: "#dce7d7",
        marriage: "#ece0e4",
        kids: "#f0e3c1",
      }[this.series] || "#e9e2d4";
    const context =
      this.mode === "daily"
        ? this.data.entry
          ? "reading"
          : "empty"
        : this.data.entries.length
          ? "archive"
          : "archiveEmpty";
    installFonts(t, this.api);
    this.updatePageBackground?.(t, this.api, device);
    this.root.innerHTML = `<style>${style}${readerBackgroundCss}${layoutCss}${safeBoxCss}${artworkScalingCss}${legacyContentCss}${responsivePageCss}.scene:has(.image-safe-box){min-height:max(640px,calc(100vw * var(--artwork-ratio,.5625) + var(--artwork-offset,0px)))}</style><section class="scene"><div class="panel"></div><div class="page-content">${renderBlocks(t, context, { series, ...this.data, updated, mode: this.mode, origin: this.api }, false, device)}</div>${renderSafeBoxes(t, context, { series, ...this.data, updated, mode: this.mode, origin: this.api }, false, device)}</section>`;
    const scene = this.root.querySelector<HTMLElement>(".scene")!;
    if (this.artworkRatio) {
      scene.style.setProperty("--artwork-ratio", String(this.artworkRatio));
      scene.style.setProperty("--artwork-aspect", `1/${this.artworkRatio}`);
    }
    this.cardObservers?.forEach((observer) => observer.disconnect());
    this.cardObservers = [
      ...this.root.querySelectorAll<HTMLElement>(".card-artboard"),
    ].map((card) =>
      trackArtworkScale(
        card,
        {
          ...t,
          artworkLayout: true,
          artworkReferenceWidth: archiveGallery(t, "desktop").cardWidth,
          artworkMobileReferenceWidth: archiveGallery(t, "mobile").cardWidth,
        },
        device,
      ),
    );
    this.artworkObserver?.disconnect();
    this.artworkObserver = trackArtworkScale(
      scene,
      t,
      device,
      this.mode === "archive",
    );
    if (!this.updatePageBackground)
      configureBackdrop(scene, t, this.api, device);
    for (const key of [
      "primary",
      "buttonBackground",
      "buttonText",
      "secondary",
      "text",
      "accent",
      "surface",
      "darkness",
      "opacity",
    ])
      scene.style.setProperty(`--${key}`, t[key] ?? "");
    scene.style.setProperty("--radius", `${t.radius}px`);
    scene.style.setProperty(
      "--heading",
      t.heading === "serif" ? "Georgia,serif" : "system-ui,sans-serif",
    );
    scene.style.setProperty("--fallback", fallback);
    scene.style.setProperty(
      "--hero-gap",
      `${t.panelTop ?? (this.series === "christmas" ? 260 : 100)}px`,
    );
    scene.style.setProperty(
      "--mobile-gap",
      `${t.panelMobileTop ?? (this.series === "christmas" ? 450 : 56)}px`,
    );
    scene.style.setProperty("--panel-x", String((t.panelX ?? 50) / 100));
    scene.style.setProperty(
      "--background-opacity",
      String(t.backgroundOpacity ?? 1),
    );
    scene.style.setProperty("--x", `${t.x}%`);
    scene.style.setProperty("--y", `${t.y}%`);
    for (const key of ["desktop", "mobile"])
      if (t[key])
        scene.style.setProperty(
          `--${key}`,
          `url("${new URL(t[key], this.api).href}")`,
        );
    scene.style.setProperty(
      "--panel-min-height",
      String(t.panelDesktopHeight ?? 0),
    );
    scene.style.setProperty(
      "--mobile-panel-min-height",
      String(t.panelMobileHeight ?? 0),
    );
    scene.style.setProperty(
      "--panel-mobile-x",
      String((t.panelMobileX ?? t.panelX ?? 50) / 100),
    );
    scene.style.setProperty(
      "--panel-percent",
      `${t.panelDesktopWidth ?? 100}%`,
    );
    scene.style.setProperty(
      "--mobile-panel-percent",
      `${t.panelMobileWidth ?? 100}%`,
    );
    scene.style.setProperty("--panel-offset", `${t.panelDesktopOffset ?? 0}%`);
    scene.style.setProperty(
      "--mobile-panel-offset",
      `${t.panelMobileOffset ?? 0}%`,
    );
    const panel = this.root.querySelector<HTMLElement>(".panel")!;
    scene.style.setProperty("--panel-width", `${t.panelWidth ?? 820}px`);
    scene.style.setProperty("--panel-padding", `${t.panelPadding ?? 64}px`);
    scene.style.setProperty(
      "--panel-mobile-padding",
      `${t.panelMobilePadding ?? 22}px`,
    );
    if (t.panelBorder === false) panel.style.border = "none";
    if (t.panelShadow === false) panel.style.boxShadow = "none";
    applyPageLayerPresentation(scene, panel, t, device);
    installMediaPlayers(this.root);
    const textOnly = this.mode === "daily" && t.scrollMode === "content";
    if (textOnly) {
      const viewportStyle = document.createElement("style");
      viewportStyle.textContent = pageViewportCss;
      this.root.append(viewportStyle);
      this.pageViewport = installPageViewport(
        this,
        this.root,
        archiveScroll,
        this.mode,
      );
    } else {
      this.responsivePage = installResponsivePage(this, this.root);
    }
    this.root
      .querySelectorAll<HTMLButtonElement>("[data-id]")
      .forEach((b) => (b.onclick = () => void this.open(b.dataset.id!, b)));
  }
  async open(id: string, trigger: HTMLElement) {
    const dialog = document.createElement("dialog");
    dialog.setAttribute("aria-label", "Previous day");
    dialog.innerHTML =
      '<button class="close" aria-label="Close reading">Close ×</button><div class="inside" aria-live="polite">Loading reading…</div>';
    this.root.querySelector(".scene")!.append(dialog);
    const scroll = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = () => dialog.close();
    dialog.querySelector<HTMLButtonElement>(".close")!.onclick = close;
    dialog.addEventListener("close", () => {
      stopMediaPlayers(dialog);
      dialog.querySelector("iframe")?.remove();
      dialog.remove();
      document.body.style.overflow = scroll;
      trigger.focus({ preventScroll: true });
    });
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (
          (e as MouseEvent).clientX < r.left ||
          (e as MouseEvent).clientX > r.right ||
          (e as MouseEvent).clientY < r.top ||
          (e as MouseEvent).clientY > r.bottom
        )
          close();
      }
    });
    dialog.showModal();
    const load = async () => {
      try {
        dialog.querySelector(".inside")!.textContent = "Loading reading…";
        const data = await this.get(`archive/${id}`);
        if (dialog.open) {
          dialog.querySelector(".inside")!.innerHTML = renderBlocks(
            this.theme,
            "archiveReading",
            {
              series: this.seriesConfig,
              entry: data.entry,
              mode: "archive",
              origin: this.api,
            },
          );
          installMediaPlayers(dialog);
          dialog
            .querySelectorAll<HTMLButtonElement>("[data-close-reading]")
            .forEach((button) => button.addEventListener("click", close));
          // Retain a close action even if the layout omits it on either device.
          const fallback = dialog.querySelector<HTMLButtonElement>(".close");
          const updateClose = () => {
            if (fallback)
              fallback.hidden = Array.from(
                dialog.querySelectorAll<HTMLButtonElement>(
                  "[data-close-reading]",
                ),
              ).some((button) => button.getClientRects().length > 0);
            if (fallback)
              fallback.style.display = fallback.hidden ? "none" : "";
          };
          updateClose();
          const closeObserver = new ResizeObserver(updateClose);
          closeObserver.observe(dialog);
          dialog.addEventListener("close", () => closeObserver.disconnect(), {
            once: true,
          });
        }
      } catch (e) {
        if (!dialog.open) return;
        dialog.querySelector(".inside")!.innerHTML =
          `<p role="alert">${escape((e as Error).message)}</p><button class="retry">Try again</button>`;
        dialog.querySelector<HTMLButtonElement>(".retry")!.onclick = () =>
          void load();
      }
    };
    await load();
  }
}
if (!customElements.get("pinedale-days"))
  customElements.define("pinedale-days", PinedaleDays);

// Subsplash removes unfamiliar HTML tags at installation time. The normal div
// mount survives that sanitization; the browser creates the custom element here.
const scriptOrigin = new URL(
  (document.currentScript as HTMLScriptElement | null)?.src || location.href,
).origin;
function mountReaders() {
  document
    .querySelectorAll<HTMLElement>(".pinedale-days-mount")
    .forEach((mount) => {
      if (mount.querySelector("pinedale-days")) return;
      // This responsive component must not inherit a desktop-only section flag.
      mount
        .closest(".sp-section.sp-hide-mobile")
        ?.classList.remove("sp-hide-mobile");
      const match = mount.id.match(
        /^pinedale-(christmas|luke|acts|marriage|kids)-(daily|archive)$/,
      );
      const reader = document.createElement("pinedale-days");
      reader.setAttribute(
        "series",
        mount.dataset.series || match?.[1] || "christmas",
      );
      reader.setAttribute("mode", mount.dataset.mode || match?.[2] || "daily");
      reader.setAttribute("api", mount.dataset.api || scriptOrigin);
      (reader as PinedaleDays).updatePageBackground =
        installPageBackground(mount);
      if ((reader as PinedaleDays).updatePageBackground)
        reader.setAttribute("site-background", "");
      mount.replaceChildren(reader);
      // Reach the viewport edges even inside a padded Subsplash content column.
      // Use clientWidth instead of 100vw so scrollbars do not cause overflow.
      mount.style.marginLeft = "0px";
      mount.style.position = "relative";
      mount.style.maxWidth = "none";
      mount.style.boxSizing = "border-box";
      const fitViewport = () => {
        const offset = Number.parseFloat(mount.style.left) || 0;
        const left = mount.getBoundingClientRect().left - offset;
        mount.style.width = document.documentElement.clientWidth + "px";
        mount.style.left = -left + "px";
      };
      fitViewport();
      window.addEventListener("resize", fitViewport);
      window.addEventListener("load", fitViewport, { once: true });
      const observer = new ResizeObserver(fitViewport);
      observer.observe(document.documentElement);
      if (mount.parentElement) observer.observe(mount.parentElement);
    });
}
if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", mountReaders, { once: true });
else mountReaders();

export {};
